import { useState, useEffect, useCallback } from "react";
import { cities } from "../data/cities";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { hasBlog, hasItinerary } from "../utils/contentAvailability";

const ITEMS_PER_PAGE = 9;

/* Only optimize Unsplash URLs to avoid breaking other CDNs */
const optimizeImage = (url = "", width = 400) => {
  if (!url) return "";
  if (url.includes("unsplash.com")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}w=${width}&auto=format&fit=crop&q=60`;
  }
  return url;
};

export default function ExploreCities() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") || "1", 10);
  
  const [query, setQuery]             = useState("");
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    setCurrentPage(urlPage);
  }, [urlPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setSearchParams(page === 1 ? {} : { page: page.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setSearchParams]);

  const filteredCities = cities.filter(
    (c) =>
      c.name.toLowerCase().includes(query.toLowerCase()) ||
      c.country.toLowerCase().includes(query.toLowerCase())
  );

  const totalPages     = Math.ceil(filteredCities.length / ITEMS_PER_PAGE);
  const paginatedCities = filteredCities.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* Mobile-safe page list (avoids too many buttons on small screens) */
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const getMobilePages = () => {
    if (totalPages <= 3) return [...Array(totalPages)].map((_, i) => i + 1);
    if (currentPage === 1) return [1, 2, 3];
    if (currentPage === totalPages) return [totalPages - 2, totalPages - 1, totalPages];
    return [currentPage - 1, currentPage, currentPage + 1];
  };

  const handleSearch = useCallback((e) => {
    setQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  /* Simulate initial data load */
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  /* ── JSON-LD: ItemList for city listing ── */
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Cities Around the World — Expeditio",
    description: "Explore travel guides for cities worldwide",
    url: "https://www.expeditio.world/cities",
    numberOfItems: cities.length,
    itemListElement: cities.slice(0, 10).map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      url: `https://www.expeditio.world/cities/${c.slug}`,
    })),
  };

  if (loading) {
    return (
      <section className="h-screen flex items-center justify-center bg-[#f6f8f5]">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-gray-300 border-t-[#5b7c67] rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-600">Loading cities…</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <Helmet>
        <title>Explore Cities Around the World | Expeditio</title>
        <meta
          name="description"
          content="Discover travel guides for 100+ cities worldwide — culture, food, history, itineraries, and local tips. Plan smarter with Expeditio."
        />
        <meta property="og:title"       content="Explore Cities Around the World | Expeditio" />
        <meta property="og:description" content="Browse city travel guides for Dubai, Tokyo, Paris, Bali, and 100+ destinations." />
        <meta property="og:type"        content="website" />
        <link rel="canonical" href={`https://www.expeditio.world/cities${currentPage > 1 ? `?page=${currentPage}` : ""}`} />
        {currentPage > 1 && <link rel="prev" href={`https://www.expeditio.world/cities${currentPage > 2 ? `?page=${currentPage - 1}` : ""}`} />}
        {currentPage < totalPages && <link rel="next" href={`https://www.expeditio.world/cities?page=${currentPage + 1}`} />}
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <section className="bg-[#f6f8f5] px-4 pt-24 pb-24 overflow-hidden">
        <div className="max-w-5xl mx-auto space-y-10">

          {/* ── HEADER ── */}
          <div className="flex items-center justify-between">
            <Link to="/" className="text-sm font-medium text-black hover:underline">
              ← Back to Home
            </Link>
          </div>

          {/* ── HERO ── */}
          <div className="bg-white rounded-[32px] p-8 md:p-10 shadow text-center space-y-4">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
              Explore Cities Around the World
            </h1>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">
              Discover destinations through culture, food, history, and local experiences.
            </p>
            <div className="max-w-md mx-auto relative">
              <input
                value={query}
                onChange={handleSearch}
                placeholder="Search city or country"
                aria-label="Search cities"
                className="w-full rounded-full border px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-[#5b7c67]/40 transition"
              />
              <span className="absolute right-4 top-3 text-gray-400">🔍</span>
            </div>
          </div>

          {/* ── CITY GRID ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedCities.map((city) => (
              <div
                key={city._id || city.id}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 group"
              >
                {/* Image — linked, with hover zoom */}
                <Link
                  to={`/cities/${city.slug}`}
                  aria-label={`${city.name} travel guide`}
                  className="block overflow-hidden"
                >
                  <div className="h-40 w-full bg-gray-100 overflow-hidden">
                    <img
                      src={optimizeImage(city.image, 400)}
                      alt={`Things to do in ${city.name}, ${city.country}`}
                      loading="lazy"
                      decoding="async"
                      className="h-40 w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>

                {/* Card body */}
                <div className="p-4 space-y-2">
                  {/* City title — keyword-rich anchor */}
                  <Link to={`/cities/${city.slug}`}>
                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-[#5b7c67] transition-colors">
                      Things to do in {city.name}
                    </h3>
                  </Link>

                  <div className="text-xs text-gray-400">📍 {city.country}</div>

                  <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                    {city.desc}
                  </p>

                  {/* CTA row — two keyword-rich links */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
                    {hasBlog(city.slug) ? (
                      <Link
                        to={`/blogs/${city.slug}-travel-guide`}
                        className="text-sm text-[#5b7c67] font-semibold hover:underline underline-offset-2 transition-colors"
                        aria-label={`${city.name} travel guide`}
                      >
                        {city.name} travel guide →
                      </Link>
                    ) : (
                      <Link
                        to={`/cities/${city.slug}`}
                        className="text-sm text-[#5b7c67] font-semibold hover:underline underline-offset-2 transition-colors"
                        aria-label={`View ${city.name} city guide`}
                      >
                        View city guide →
                      </Link>
                    )}
                    {hasItinerary(city.slug) ? (
                      <Link
                        to={`/itineraries/${city.slug}`}
                        className="text-xs text-gray-400 hover:text-[#5b7c67] transition-colors font-medium"
                        aria-label={`View ${city.name} itinerary`}
                      >
                        View itinerary →
                      </Link>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50 px-2 py-1 rounded-full font-medium">
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── EMPTY STATE ── */}
          {filteredCities.length === 0 && (
            <div className="text-center text-sm text-gray-500 pt-10">
              No cities found for "<strong>{query}</strong>"
            </div>
          )}

          {/* ── PAGINATION ── */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 pt-6">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="px-5 py-3 sm:px-4 sm:py-2 rounded-full border text-base sm:text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                ← Prev
              </button>

              {(isMobile
                ? getMobilePages()
                : [...Array(totalPages)].map((_, i) => i + 1)
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-11 h-11 sm:w-9 sm:h-9 rounded-full font-medium text-base sm:text-sm transition-colors
                    ${currentPage === page
                      ? "bg-[#5b7c67] text-white shadow-sm"
                      : "border hover:bg-gray-100"
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="px-5 py-3 sm:px-4 sm:py-2 rounded-full border text-base sm:text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                Next →
              </button>
            </div>
          )}

        </div>
      </section>
    </>
  );
}
